import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api, { getToken } from "../lib/api";
import BrandHeader from "../components/BrandHeader";
import BrandFooter from "../components/BrandFooter";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card } from "../components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Loader2, ChevronLeft, Download, Filter, ShieldCheck, RefreshCw, Eye, User, X,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Colour palette for event source badges. Matches the tone used in
// Settings → History for consistency across admin surfaces.
const METHOD_COLOR = {
  GET:    "bg-slate-100 border-slate-200 text-slate-700",
  POST:   "bg-sky-50 border-sky-200 text-sky-800",
  PUT:    "bg-amber-50 border-amber-200 text-amber-800",
  PATCH:  "bg-amber-50 border-amber-200 text-amber-800",
  DELETE: "bg-rose-50 border-rose-200 text-rose-800",
};

const STATUS_COLOR = (code) => {
  if (code == null) return "bg-slate-100 text-slate-500";
  if (code < 300) return "bg-emerald-50 border border-emerald-200 text-emerald-700";
  if (code < 400) return "bg-sky-50 border border-sky-200 text-sky-700";
  if (code < 500) return "bg-amber-50 border border-amber-200 text-amber-800";
  return "bg-rose-50 border border-rose-200 text-rose-700";
};

export default function AdminAuditLog() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [summary, setSummary] = useState({ total: 0, events: [], top_actors: [] });
  const [filters, setFilters] = useState({ event: "", actor: "", method: "" });
  const [detail, setDetail] = useState(null); // open row detail dialog
  const [exporting, setExporting] = useState(false);

  const buildQuery = useCallback((extra = {}) => {
    const q = new URLSearchParams();
    if (filters.event) q.set("event", filters.event);
    if (filters.actor) q.set("actor", filters.actor);
    if (filters.method) q.set("method", filters.method);
    Object.entries(extra).forEach(([k, v]) => v && q.set(k, v));
    return q.toString();
  }, [filters]);

  const load = useCallback(async (append = false, cursor = null) => {
    if (append) setLoadingMore(true); else setLoading(true);
    try {
      const qs = buildQuery({ limit: 50, before: cursor });
      const r = await api.get(`/audit-log?${qs}`);
      setItems(prev => append ? [...prev, ...r.data.items] : r.data.items);
      setHasMore(!!r.data.has_more);
      setNextCursor(r.data.next_cursor);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit log");
    } finally {
      if (append) setLoadingMore(false); else setLoading(false);
    }
  }, [buildQuery]);

  const loadSummary = useCallback(async () => {
    try {
      const r = await api.get("/audit-log/summary");
      setSummary({ total: r.data.total, events: r.data.events, top_actors: r.data.top_actors });
    } catch (e) {
      // non-fatal — filters just won't populate
    }
  }, []);

  // Reset the list any time filters change.
  useEffect(() => { load(false, null); }, [load]);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  const clearFilters = () => setFilters({ event: "", actor: "", method: "" });
  const activeFilterCount = [filters.event, filters.actor, filters.method].filter(Boolean).length;

  const exportCsv = async () => {
    setExporting(true);
    try {
      const qs = buildQuery();
      // Use fetch directly to preserve the streaming CSV response
      const resp = await fetch(`${API}/audit-log/export.csv?${qs}`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!resp.ok) throw new Error(`Export failed: ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `sentry-audit-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Audit log CSV downloaded");
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const eventOptions = useMemo(() => summary.events || [], [summary.events]);
  const topActorOptions = useMemo(() => summary.top_actors || [], [summary.top_actors]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col" data-testid="audit-log-page">
      <BrandHeader showSettings showSecurityEvents showImport showClients showLogout>
        <Button variant="ghost" onClick={() => nav("/admin")} className="text-white/80 hover:text-white hover:bg-white/10" data-testid="back-to-admin-btn">
          <ChevronLeft size={16} className="mr-1"/>Back
        </Button>
      </BrandHeader>

      <div className="flex-1 max-w-7xl mx-auto w-full px-6 py-8">
        {/* Header row: title + count + actions */}
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <ShieldCheck className="text-brand-primary" size={28}/>
              Audit log
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Immutable record of every admin action and sensitive read.
              Rows are auto-pruned after 365 days per SOC2 CC7.2 retention policy.
              Total rows: <span className="font-semibold" data-testid="audit-total-count">{summary.total.toLocaleString()}</span>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="outline"
              onClick={() => { load(false, null); loadSummary(); }}
              disabled={loading}
              className="border-slate-300 text-slate-700"
              data-testid="audit-refresh-btn"
            >
              <RefreshCw size={14} className={`mr-1 ${loading ? "animate-spin" : ""}`}/>Refresh
            </Button>
            <Button
              onClick={exportCsv}
              disabled={exporting}
              className="bg-brand-primary hover:bg-brand-primary/90 text-white"
              data-testid="audit-export-csv-btn"
            >
              {exporting ? <><Loader2 className="animate-spin mr-2" size={14}/>Exporting…</> : <><Download size={14} className="mr-2"/>Export CSV</>}
            </Button>
          </div>
        </div>

        {/* Filter bar */}
        <Card className="p-4 mb-6 shadow-sm border-slate-200" data-testid="audit-filter-bar">
          <div className="flex items-center gap-2 mb-2">
            <Filter size={16} className="text-slate-500"/>
            <span className="text-sm font-semibold text-slate-700">Filters</span>
            {activeFilterCount > 0 && (
              <span className="text-[11px] text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
                {activeFilterCount} active
              </span>
            )}
            {activeFilterCount > 0 && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto text-slate-500 hover:text-slate-800" data-testid="audit-clear-filters-btn">
                <X size={13} className="mr-1"/>Clear
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wide font-medium text-slate-500">Event</label>
              <Select
                value={filters.event || "__all__"}
                onValueChange={(v) => setFilters(f => ({ ...f, event: v === "__all__" ? "" : v }))}
              >
                <SelectTrigger className="mt-1" data-testid="audit-filter-event">
                  <SelectValue placeholder="All events"/>
                </SelectTrigger>
                <SelectContent className="max-h-96">
                  <SelectItem value="__all__">All events</SelectItem>
                  {eventOptions.map(e => (
                    <SelectItem key={e} value={e}>{e}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide font-medium text-slate-500">Actor (email contains…)</label>
              <Input
                value={filters.actor}
                onChange={(e) => setFilters(f => ({ ...f, actor: e.target.value }))}
                placeholder="e.g., admin"
                className="mt-1"
                data-testid="audit-filter-actor"
              />
              {topActorOptions.length > 0 && !filters.actor && (
                <div className="mt-1 flex flex-wrap gap-1">
                  {topActorOptions.slice(0, 5).map(a => (
                    <button
                      key={a.actor_email}
                      onClick={() => setFilters(f => ({ ...f, actor: a.actor_email }))}
                      className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 border border-slate-200 text-slate-700 hover:bg-slate-200"
                      data-testid={`audit-quick-actor-${a.actor_email}`}
                    >
                      {a.actor_email} <span className="text-slate-500">({a.count})</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide font-medium text-slate-500">Method</label>
              <Select
                value={filters.method || "__all__"}
                onValueChange={(v) => setFilters(f => ({ ...f, method: v === "__all__" ? "" : v }))}
              >
                <SelectTrigger className="mt-1" data-testid="audit-filter-method">
                  <SelectValue placeholder="Any"/>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Any</SelectItem>
                  {["GET", "POST", "PATCH", "PUT", "DELETE"].map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Table */}
        <Card className="p-0 overflow-hidden shadow-sm border-slate-200" data-testid="audit-table-card">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-slate-500">
              <Loader2 className="animate-spin mr-2" size={16}/>Loading audit log…
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-16 text-slate-500" data-testid="audit-empty">
              No audit rows match the current filters.
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="audit-table">
                  <thead className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wide text-slate-600">
                    <tr>
                      <th className="text-left px-4 py-2 font-semibold">Timestamp</th>
                      <th className="text-left px-4 py-2 font-semibold">Actor</th>
                      <th className="text-left px-4 py-2 font-semibold">Event</th>
                      <th className="text-left px-4 py-2 font-semibold">Method</th>
                      <th className="text-left px-4 py-2 font-semibold">Path</th>
                      <th className="text-left px-4 py-2 font-semibold">Status</th>
                      <th className="text-left px-4 py-2 font-semibold">Duration</th>
                      <th className="text-left px-4 py-2 font-semibold"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {items.map(row => (
                      <tr key={row.id} className="hover:bg-slate-50" data-testid={`audit-row-${row.id}`}>
                        <td className="px-4 py-2 whitespace-nowrap text-slate-700 font-mono text-[11px]">
                          {row.timestamp ? new Date(row.timestamp).toLocaleString() : "—"}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-slate-800">
                          <div className="flex items-center gap-1">
                            <User size={11} className="text-slate-400"/>
                            <span className={row.actor_email === "anonymous" ? "text-rose-600 italic" : ""}>{row.actor_email}</span>
                          </div>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span className="text-[11px] font-mono bg-brand-primary/5 border border-brand-primary/20 text-brand-primary rounded px-1.5 py-0.5">
                            {row.event}
                          </span>
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap">
                          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded border ${METHOD_COLOR[row.method] || METHOD_COLOR.GET}`}>
                            {row.method}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-slate-600 font-mono text-[11px] max-w-[280px] truncate" title={row.path}>{row.path}</td>
                        <td className="px-4 py-2">
                          <span className={`text-[10px] font-mono px-1.5 py-0.5 rounded ${STATUS_COLOR(row.status_code)}`}>
                            {row.status_code ?? "—"}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-[11px] text-slate-500 whitespace-nowrap">
                          {row.duration_ms != null ? `${row.duration_ms}ms` : "—"}
                        </td>
                        <td className="px-2 py-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setDetail(row)}
                            className="text-slate-500 hover:text-brand-primary"
                            data-testid={`audit-detail-btn-${row.id}`}
                            title="View request details"
                          >
                            <Eye size={13}/>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Load more */}
              <div className="border-t border-slate-100 p-3 flex items-center justify-between bg-slate-50">
                <span className="text-[11px] text-slate-500">Showing {items.length} rows</span>
                {hasMore && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => load(true, nextCursor)}
                    disabled={loadingMore}
                    className="border-slate-300 text-slate-700"
                    data-testid="audit-load-more-btn"
                  >
                    {loadingMore ? <><Loader2 className="animate-spin mr-2" size={12}/>Loading…</> : "Load more"}
                  </Button>
                )}
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Row detail dialog — shows full redacted request_summary */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="audit-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="text-brand-primary" size={18}/>
              Audit row
            </DialogTitle>
            <DialogDescription>
              {detail?.timestamp && new Date(detail.timestamp).toLocaleString()} · <span className="font-mono">{detail?.event}</span>
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="overflow-y-auto space-y-3 pr-1">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div><span className="text-slate-500">Actor</span><div className="font-mono">{detail.actor_email} ({detail.actor_role})</div></div>
                <div><span className="text-slate-500">Method / Status</span><div className="font-mono">{detail.method} → {detail.status_code}</div></div>
                <div className="col-span-2"><span className="text-slate-500">Path</span><div className="font-mono text-slate-800 break-all">{detail.path}</div></div>
                <div><span className="text-slate-500">Duration</span><div className="font-mono">{detail.duration_ms}ms</div></div>
                <div><span className="text-slate-500">IP</span><div className="font-mono">{detail.ip || "—"}</div></div>
                <div className="col-span-2"><span className="text-slate-500">User agent</span><div className="font-mono text-[11px] break-all">{detail.user_agent || "—"}</div></div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1">Request body (redacted)</div>
                <pre className="text-[11px] font-mono bg-slate-900 text-emerald-200 rounded p-3 overflow-x-auto max-h-72" data-testid="audit-detail-body">
                  {detail.request_summary != null ? JSON.stringify(detail.request_summary, null, 2) : "— none —"}
                </pre>
                <p className="text-[10px] text-slate-400 mt-1">Secrets (API keys, passwords, webhook URLs) are always masked with •[6] before storage.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetail(null)} data-testid="audit-detail-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BrandFooter/>
    </div>
  );
}
